import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { accountDevicesRoute, extensionsRoute } from 'src/app/app-routing.module';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { DeviceDetailSaveEvent } from 'src/app/pages/device-details/models/device-detail-form-submit-event';
import { DevicePrivileges } from '../../models/device-privileges';
import { DeviceOrderingOptions, DeviceRegisterOptions } from '../../models/device-register-options';
import { DeviceRegisterListItem } from '../../models/device-register-results.model';
import { DeviceTypeOption } from '../../models/device-type-option.interface';
import { selectOrderingOptions, selectQuickFilters } from '../../state/device-register.reducer';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-account-device-register-view',
    styleUrls: ['./account-device-register-view.component.scss'],
    templateUrl: './account-device-register-view.component.html',
    standalone: false
})
export class AccountDeviceRegisterViewComponent extends EntityRegisterView<
   DeviceRegisterListItem,
   DeviceRegisterOptions,
   DeviceOrderingOptions
> {
   protected selectQuickFilters(): (state: object) => DeviceRegisterOptions | null {
      return selectQuickFilters;
   }

   protected override onQuickFiltersInit(quickFilters: DeviceRegisterOptions): void {
      if (quickFilters) {
         this.selectedDeviceType = quickFilters.typeName;
         this.hideUsedDevices = quickFilters.hideUsedDevices;
         this.showDevicesWithIssues = quickFilters.showDevicesWithIssues;
      }
   }
   @Input() includeInactive: boolean;

   @Input() devicePrivileges: DevicePrivileges;

   @Input() isSystemUser: boolean;

   @Output() hideUsedDevicesChange = new EventEmitter<Boolean>();

   @Output() resetHackedAndFraud: EventEmitter<DeviceRegisterListItem> = new EventEmitter<DeviceRegisterListItem>();

   @Output() rebootPhone = new EventEmitter<number>();

   @Output() resetPhoneZtp = new EventEmitter<number>();

   @Output() deviceReleased = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() releaseToAccount = new EventEmitter<DeviceRegisterListItem>();

   @Output() deleteDevice = new EventEmitter<{ deviceId: number }>();

   getRouterLink(id: number) {
      return `/${accountDevicesRoute}/${id}`;
   }

   ngOnInit() {
      super.ngOnInit();
      this.store.pipe(select(selectOrderingOptions), take(1)).subscribe((orderingOptions) => {
         if (orderingOptions) {
            this.orderingOptions = orderingOptions;
         }
      });
   }

   getDeviceExtensionRouterLink(device: DeviceRegisterListItem) {
      return `/${extensionsRoute}/${device.phoneExtension?.extensionId}`;
   }

   hideUsedDevices: boolean | undefined = false;

   showDevicesWithIssues: boolean | undefined = false;

   selectedDeviceType: DeviceTypeOption | undefined = undefined;

   constructor(store: Store, private clipboard: Clipboard, private snackbar: MatSnackBar) {
      super(store);

      this.resetHackedAndFraud = new EventEmitter<DeviceRegisterListItem>();
   }

   toggleHideUsedDevices(event) {
      this.registerOptions = {
         ...this.registerOptions,
         hideUsedDevices: event,
      };
      this.quickFilterChange.emit(this.registerOptions);
      this.hideUsedDevicesChange.emit(event);
   }

   toggleShowDevicesWithIssues(event) {
      this.registerOptions = {
         ...this.registerOptions,
         showDevicesWithIssues: event,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onDeviceTypeSelected(deviceType: DeviceTypeOption) {
      this.registerOptions = { ...this.registerOptions, typeName: deviceType };
      this.quickFilterChange.emit(this.registerOptions);
   }

   resetHackedAndFraudDevice(device: DeviceRegisterListItem) {
      this.resetHackedAndFraud.emit(device);
   }

   rebootPhoneClick(device: DeviceRegisterListItem) {
      if (device.deviceId) {
         this.rebootPhone.emit(device.deviceId);
      }
   }

   resetPhoneZtpClick(device: DeviceRegisterListItem) {
      if (device.deviceId) this.resetPhoneZtp.emit(device.deviceId);
   }

   copyMacAddressToClipboard(macAddress: string) {
      const copied: boolean = this.clipboard.copy(macAddress);

      if (copied) {
         this.snackbar.open('Copied to Clipboard');
      }
   }
   copySipUsernameToClipboard(entity: DeviceRegisterListItem) {
      if (entity && entity.sipUsers && entity.sipUsers[0].userName) {
         const copied: boolean = this.clipboard.copy(entity.sipUsers[0].userName);

         if (copied) {
            this.snackbar.open('Copied to Clipboard');
         } else {
            this.snackbar.open('Failed to Copy');
         }
      }
   }

   copyWebInterfaceUrl(device: DeviceRegisterListItem) {
      const handset = device?.basestationHandsets?.[0];
      const protocol: 'http' | 'https' = handset ? 'http' : 'https';
      let ip = device?.sipUsers?.[0]?.internalIps ?? device?.sipUsers?.[0]?.externalIps;

      if (handset) {
         ip =
            device?.basestationHandsets?.[0].phone.sipUsersConnection.items[0].internalIps ??
            device?.basestationHandsets?.[0].phone.sipUsersConnection.items[0].externalIps;
      }

      if (!ip) return;

      let url = `${protocol}://${ip}`;
      const copied = this.clipboard.copy(url);
      if (copied) {
         this.snackbar?.open('URL copied to clipboard.');
      }
   }

   copyCurrentWebInterfacePassword(device: DeviceRegisterListItem) {
      let password = device?.sipUsers?.[0].phone.adminPassword;

      if (device?.basestationHandsets?.[0]) password = device?.basestationHandsets?.[0].phone.adminPassword;
      if (!password) return;

      const copied = this.clipboard.copy(password);
      if (copied) {
         this.snackbar.open('Password copied to clipboard.');
      }
   }

   canReleaseToAccount(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canEdit && device.phoneExtension && this.isSystemUser;
   }

   canReleaseToSystem(device: DeviceRegisterListItem) {
      return (
         this.devicePrivileges.canEdit &&
         this.devicePrivileges.canDelete &&
         this.isSystemUser$ &&
         device.accountName != null
      );
   }

   canDeleteFromNDT(device: DeviceRegisterListItem) {
      return this.devicePrivileges.canDelete && this.isSystemUser$;
   }

   onReleaseToSystemClick(device: DeviceRegisterListItem): void {
      const releaseEventModel: DeviceDetailSaveEvent = {
         device: {
            accountId: null,
            deviceId: device?.deviceId ?? null,
         },
         deviceFields: ['accountId'],
      };

      this.deviceReleased.emit(releaseEventModel);
   }

   onReleaseToAccountClick(device: DeviceRegisterListItem): void {
      this.releaseToAccount.emit(device);
   }

   onDeleteClick(device: DeviceRegisterListItem): void {
      if (device?.deviceId) {
         this.deleteDevice.emit({
            deviceId: device.deviceId,
         });
      }
   }
}
